import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, FileText, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
    ApiCallError, getHandouts, saveHandout, deleteHandout, uploadHandoutRevision,
    type HandoutDto,
} from '@/api/client';
import type { LookupsDto } from '@/api/types';

/**
 * THE HANDOUT LIBRARY — standing documents (#317 step 5).
 *
 * Pete, 2026-08-27: *"On outbounds, we will need to print directions to where the
 * Driver is going too as a .pdf... We give the driver two copies of the BOL and
 * the directions to where they are taking the load."*
 *
 * ---- ⭐⭐ THESE ARE TEMPLATES, NOT LOAD PAPERWORK --------------------------
 *
 * ⛔ THE SCREEN HAS TO MAKE THAT DISTINCTION OBVIOUS, because everything else in
 * this app that looks like a document IS a record of one load. A handout is the
 * same PDF every time — editing it changes what every future driver is handed.
 *
 * ---- ⛔ REVISIONS, NOT EDITS ----------------------------------------------
 *
 * Pete: *"If that docs changes later, then maybe it has a revision."*
 *
 * ⚠️ SO THERE IS NO "REPLACE THE FILE" BUTTON, ANYWHERE. Uploading adds revision
 * N+1 and leaves everything before it exactly where it is. A load filed in March
 * cites revision 1, and revision 1 has to still be there for that citation to
 * mean anything.
 *
 * ---- ⛔ AND DELETING IS REFUSED ONCE ANYTHING HAS BEEN UPLOADED ------------
 *
 * ⚠️ A revision is EVIDENCE. The server refuses, and this screen offers the
 * reversible alternative first — untick Active, which stops it being offered
 * without erasing what it explains.
 */

interface Props {
    getToken: () => Promise<string>;
    lookups: LookupsDto;
}

const BLANK = (): HandoutDto => ({
    handoutId: '',
    name: '',
    documentType: '',
    companyCode: null,
    facilityCode: null,
    clientCode: null,
    consigneeCode: null,
    copies: 1,
    priority: 100,
    notes: null,
    isActive: true,
    currentRevision: null,
    revisionCount: 0,
});

const orNull = (v: string): string | null => (v.trim().length === 0 ? null : v.trim());
const kb = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export default function HandoutAdmin({ getToken, lookups }: Props) {
    const [rows, setRows] = useState<HandoutDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState<HandoutDto | null>(null);
    const [removing, setRemoving] = useState<HandoutDto | null>(null);
    const [changeNote, setChangeNote] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setRows((await getHandouts(await getToken())).handouts);
            setError(null);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not read the handout library.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { void load(); }, [load]);

    async function save() {
        if (!draft) return;
        setBusy(true);
        setError(null);
        setSaved(null);
        try {
            const r = await saveHandout(await getToken(), {
                ...draft,
                handoutId: draft.handoutId || null,
            });
            await load();
            /* ⭐ THE DRAFT STAYS OPEN ON A NEW HANDOUT, carrying its new id — because
               the very next thing anybody wants is to upload the file, and closing
               the form would make that a second hunt through the list. */
            setDraft(d => (d ? { ...d, handoutId: r.handoutId } : d));
            setSaved('Saved.');
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not save that handout.');
        } finally {
            setBusy(false);
        }
    }

    async function upload(file: File) {
        if (!draft?.handoutId) return;
        setBusy(true);
        setError(null);
        setSaved(null);
        try {
            const r = await uploadHandoutRevision(
                await getToken(), draft.handoutId, file, orNull(changeNote));
            await load();
            setChangeNote('');
            /* ⭐ THE REVISION NUMBER IS THE USEFUL ANSWER, not "saved". It is what a
               load's record cites and what an admin tells a client. */
            setSaved(`Uploaded as revision ${r.revision}.`);
            const fresh = (await getHandouts(await getToken())).handouts
                .find(h => h.handoutId === draft.handoutId);
            if (fresh) setDraft(fresh);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not upload that file.');
        } finally {
            setBusy(false);
        }
    }

    async function runDelete() {
        if (!removing) return;
        setBusy(true);
        try {
            await deleteHandout(await getToken(), removing.handoutId);
            await load();
            setRemoving(null);
            if (draft?.handoutId === removing.handoutId) setDraft(null);
        } catch (err) {
            /* ⚠️ A REFUSAL HERE IS A STATE, NOT A MISTAKE — the handout is in use.
               The server's message names the reversible alternative, so it is shown
               verbatim rather than replaced with "could not delete". */
            setError(err instanceof ApiCallError ? err.details.error : 'Could not delete that handout.');
            setRemoving(null);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-sm font-semibold">Handouts</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                    The things you hand the driver — directions, safety sheets, gate passes.
                    The <strong>same PDF every time</strong>, not paperwork for one appointment.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                    Scoped by consignee, client, facility and company — <strong>the consignee
                    wins</strong>, because directions belong to where the appointment is going rather
                    than to whose freight it is. Only one handout per document type reaches a
                    load; different types never compete.
                </p>
                {/**
                  * ⛔ THE REVISION RULE IS STATED BEFORE ANYBODY UPLOADS. Somebody
                  * expecting "replace the file" needs to know they are adding to a
                  * history, not overwriting one.
                  */}
                <p className="mt-1 text-xs text-muted-foreground">
                    Uploading a file adds a <em>revision</em>. Older revisions stay — an appointment
                    filed months ago records which one its driver was given, and that citation
                    has to keep pointing at something.
                </p>
            </div>

            {error && (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {error}
                </div>
            )}
            {saved && !error && (
                <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    {saved}
                </div>
            )}

            <div className="space-y-1">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">The library</span>
                    {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </div>
                {rows.length === 0 && !loading && (
                    <div className="rounded border border-dashed px-3 py-2 text-xs text-muted-foreground">
                        Nothing yet. A <em>standing</em> requirement on a departure checklist looks
                        here for a handout with a matching document type.
                    </div>
                )}
                <div className="divide-y rounded border">
                    {rows.map(h => (
                        <button
                            key={h.handoutId}
                            type="button"
                            onClick={() => { setDraft({ ...h }); setSaved(null); setChangeNote(''); }}
                            className={'flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-muted/50 '
                                + (draft?.handoutId === h.handoutId ? 'bg-muted' : '')}
                        >
                            <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                                <div className="font-medium">
                                    {h.name}
                                    {!h.isActive && <span className="ml-1 text-muted-foreground">(off)</span>}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    {h.documentType}
                                    {' · '}
                                    {[h.consigneeCode && `to ${h.consigneeCode}`,
                                      h.clientCode && `client ${h.clientCode}`,
                                      h.facilityCode && `facility ${h.facilityCode}`,
                                      h.companyCode && `company ${h.companyCode}`]
                                        .filter(Boolean).join(' + ') || 'everything'}
                                    {' · '}
                                    {h.currentRevision
                                        ? `rev ${h.currentRevision.revision}`
                                        : 'no file yet'}
                                </div>
                            </div>
                            {/* ⚠️ A HANDOUT WITH NO FILE CANNOT BE GIVEN TO ANYBODY, and it
                                is easy to create one and forget the upload. Said on the row
                                rather than only inside the editor. */}
                            {!h.currentRevision && (
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                            )}
                        </button>
                    ))}
                </div>
                <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => { setDraft(BLANK()); setSaved(null); setChangeNote(''); }}
                >
                    <Plus className="mr-1 h-3.5 w-3.5" /> New handout
                </Button>
            </div>

            {draft && (
                <div className="space-y-3 rounded border p-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="h-name">Name</Label>
                            <Input
                                id="h-name" value={draft.name} maxLength={200}
                                placeholder="Hellman Aurora directions"
                                onChange={e => setDraft({ ...draft, name: e.target.value })}
                                className="mt-1.5"
                            />
                        </div>
                        <div>
                            <Label htmlFor="h-type">Document type</Label>
                            <Input
                                id="h-type" value={draft.documentType} maxLength={100}
                                placeholder="DIRECTIONS"
                                onChange={e => setDraft({ ...draft, documentType: e.target.value })}
                                className="mt-1.5"
                            />
                            {/* ⭐ IT SAYS WHAT THE FIELD IS FOR. "Document type" alone is
                                true and useless; this is the join to the checklist. */}
                            <p className="mt-1 text-[10px] text-muted-foreground">
                                How a <em>standing</em> checklist requirement finds this, and how the
                                filed copy is labelled on the appointment.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="h-consignee">Consignee</Label>
                            <Input
                                id="h-consignee" value={draft.consigneeCode ?? ''} maxLength={100}
                                placeholder="Any consignee"
                                onChange={e => setDraft({ ...draft, consigneeCode: orNull(e.target.value) })}
                                className="mt-1.5"
                            />
                            <p className="mt-1 text-[10px] text-muted-foreground">
                                The most specific level. Leave blank for a client-wide sheet.
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="h-client">Client</Label>
                            <select
                                id="h-client" value={draft.clientCode ?? ''}
                                onChange={e => setDraft({ ...draft, clientCode: orNull(e.target.value) })}
                                className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                            >
                                <option value="">Any client</option>
                                {lookups.clients.map(c => (
                                    <option key={c.ClientCode} value={c.ClientCode}>{c.ClientName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="h-fac">Facility</Label>
                            <select
                                id="h-fac" value={draft.facilityCode ?? ''}
                                onChange={e => setDraft({ ...draft, facilityCode: orNull(e.target.value) })}
                                className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                            >
                                <option value="">Any facility</option>
                                {lookups.facilities.map(f => (
                                    <option key={f.FacilityCode} value={f.FacilityCode}>{f.DisplayName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="h-copies">Print how many</Label>
                            <Input
                                id="h-copies" type="number" min={1} max={20} value={draft.copies}
                                onChange={e => setDraft({ ...draft, copies: Number(e.target.value) || 1 })}
                                className="mt-1.5"
                            />
                            {draft.copies > 1 && (
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    An instruction for the print button. Nothing here can see how many
                                    sheets came out.
                                </p>
                            )}
                        </div>
                        <div className="col-span-2 flex items-end gap-3">
                            <label className="flex items-center gap-1.5 text-xs">
                                <input
                                    type="checkbox" checked={draft.isActive}
                                    onChange={e => setDraft({ ...draft, isActive: e.target.checked })}
                                />
                                Active
                            </label>
                        </div>
                    </div>

                    {/* ---- revisions ------------------------------------- */}
                    <div className="space-y-1.5 border-t pt-3">
                        <div className="text-xs font-medium">The file</div>
                        {!draft.handoutId ? (
                            /* ⛔ THE UPLOAD NEEDS AN ID, so it cannot be offered before the
                               first save. Said plainly rather than shown disabled. */
                            <p className="text-[11px] text-muted-foreground">
                                Save this handout first, then upload its file.
                            </p>
                        ) : (
                            <>
                                {draft.currentRevision ? (
                                    <div className="rounded border bg-muted/40 px-2 py-1.5 text-[11px]">
                                        <span className="font-medium">
                                            Revision {draft.currentRevision.revision}
                                        </span>
                                        {' · '}{draft.currentRevision.fileName}
                                        {' · '}{kb(draft.currentRevision.bytes)}
                                        {draft.revisionCount > 1 && (
                                            <span className="text-muted-foreground">
                                                {' '}({draft.revisionCount} revisions kept)
                                            </span>
                                        )}
                                        {draft.currentRevision.changeNote && (
                                            <div className="text-muted-foreground">
                                                {draft.currentRevision.changeNote}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="flex items-start gap-1 text-[11px] text-amber-700">
                                        <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                                        No file uploaded yet, so this cannot be given to anyone.
                                    </p>
                                )}

                                <Input
                                    value={changeNote}
                                    maxLength={400}
                                    placeholder="What changed? e.g. Aurora moved the gate"
                                    onChange={e => setChangeNote(e.target.value)}
                                    className="h-8 text-xs"
                                />
                                {/* ⭐ THE NOTE IS WHY TWO LOADS A WEEK APART GOT DIFFERENT
                                    DIRECTIONS. Optional, but asked for at the moment it is
                                    known rather than reconstructed later. */}
                                <input
                                    ref={fileRef}
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        e.target.value = '';
                                        if (f) void upload(f);
                                    }}
                                />
                                <Button
                                    type="button" variant="outline" size="sm" disabled={busy}
                                    onClick={() => fileRef.current?.click()}
                                >
                                    <Upload className="mr-1 h-3.5 w-3.5" />
                                    {draft.currentRevision
                                        ? `Upload revision ${(draft.currentRevision.revision) + 1}`
                                        : 'Upload the file'}
                                </Button>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-2 border-t pt-3">
                        <Button type="button" onClick={() => void save()} disabled={busy}>
                            {busy ? 'Saving…' : 'Save'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setDraft(null)} disabled={busy}>
                            Close
                        </Button>
                        {draft.handoutId && (
                            <Button
                                type="button" variant="outline" disabled={busy}
                                onClick={() => setRemoving(draft)}
                                className="ml-auto text-destructive"
                            >
                                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={removing !== null}
                title="Delete this handout?"
                message={
                    `"${removing?.name ?? ''}" is removed from the library.\n\n`
                    + 'If any file has been uploaded against it this will be refused — appointments may '
                    + 'cite those revisions to show which version their driver was given. Untick '
                    + 'Active instead; it stops being offered without erasing what it explains.'
                }
                confirmLabel="Delete it"
                destructive
                busy={busy}
                onConfirm={() => void runDelete()}
                onCancel={() => setRemoving(null)}
            />
        </div>
    );
}
